// Игра по переписке на клиенте: список моих партий и открытых наборов (POST api/corr, от имени устройства — вход в
// партию годится с любого связанного устройства), создание и вход (только с включёнными уведомлениями), открытие
// партии — обычная онлайн-комната без запоминания сессии. Уведомление, пришедшее, пока приложение на экране,
// service worker отдаёт сюда (onCorrPush): лента, ящик и свежий список. Сервер — server/corr.go.
import type { CorrGame, CorrList } from '../types.ts';
import { clientId, deviceKey } from './outbox.ts';
import { st, ui, save, toast } from './store.ts';
import { startOnline } from './flow.ts';
import { markRead, pushItem, corrItem } from './inbox.ts';
import { notify } from './notify.ts';
import { esc } from './cards.ts';

function isCorrList(x: unknown): x is CorrList {
  const o = x as Partial<CorrList> | null;
  return !!o && Array.isArray(o.games) && Array.isArray(o.lobbies);
}

/** Загрузить мои партии по переписке; repaint — перерисовать, когда ответ пришёл */
export async function loadCorr(repaint: () => void): Promise<void> {
  if (st.corr.loading) return;
  st.corr.loading = true;
  repaint();
  try {
    const r = await fetch('api/corr', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client: clientId(), key: deviceKey() }) });
    const j: unknown = r.ok ? await r.json() : null;
    if (!isCorrList(j)) throw new Error('bad corr');
    st.corr.data = j;
    st.corr.error = false;
  } catch {
    st.corr.error = true;
  } finally {
    st.corr.loading = false;
    repaint();
  }
}

/** Без уведомлений в партию по переписке не войти: сказать, что включить */
function needPush(): boolean {
  if (st.push.status === 'on') return false;
  toast('Сначала включи уведомления — без них партия тебя не дозовётся', 4000);
  return true;
}

/** Создать партию по переписке на size мест */
export function createCorr(size: number): void {
  if (needPush()) return;
  startOnline('create', { size });
}

/** Войти в открытый набор (или по коду) */
export function joinCorr(code: string): void {
  if (needPush()) return;
  startOnline('join', { code });
}

/** Открыть свою партию из списка */
export function openCorr(code: string): void {
  const g = st.corr.data?.games.find((x) => x.code === code);
  if (!g) {
    toast('Партия не найдена — обнови список');
    return;
  }
  markRead((i) => i.kind === 'corr' && i.code === code);
  startOnline('corr', { session: { code: g.code, token: g.token, pid: g.me, name: g.name } });
}

/**
 * Уведомление, пришедшее при открытом приложении (service worker не показал его в шторке): в ленту и ящик, список
 * партий — свежий. Если эта партия уже на экране — ход и так виден, сообщать не о чем.
 */
export function onCorrPush(m: { title?: string; body?: string; code?: string }, repaint: () => void): void {
  void loadCorr(repaint);
  // не про партию — просто показать (ничего пришедшее не должно пропадать молча)
  if (!m.code) {
    notify({ html: `🔔 <b>${esc(m.title ?? 'Крутагидон')}</b> — ${esc(m.body ?? '')}`, cls: 'n-note', hold: 6000, priority: 'high', sound: 'bell' });
    return;
  }
  if (st.net?.code === m.code) return;
  const code = m.code;
  pushItem(corrItem(code, m.title ?? 'Партия по переписке', m.body ?? ''));
  notify({ html: `✉️ <b>${esc(m.title ?? '')}</b> — ${esc(m.body ?? '')}`, cls: 'n-note', hold: 8000, priority: 'high', sound: 'bell', onTap: () => void openCorrFromLink(code, repaint) });
}

/**
 * Игра по переписке при запуске: сообщения service worker (уведомление при открытом приложении, тап по уведомлению)
 * и вход по ссылке ?corr=КОД (уведомление открыло приложение заново или друг прислал ссылку на набор).
 */
export function initCorr(repaint: () => void): void {
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (e: MessageEvent<unknown>) => {
      const d = e.data as { type?: string; msg?: { title?: string; body?: string; code?: string }; code?: string } | null;
      if (d?.type === 'push' && d.msg) onCorrPush(d.msg, repaint);
      if (d?.type === 'openCorr' && d.code) void openCorrFromLink(d.code, repaint);
    });
  }
  const u = new URL(location.href);
  const code = u.searchParams.get('corr')?.toUpperCase();
  if (code) {
    u.searchParams.delete('corr');
    history.replaceState(history.state, '', u.href);
    ui.corrFocus = code;
    void openCorrFromLink(code, repaint);
  } else void loadCorr(repaint);
}

/** Сколько моих партий ждут моего решения (подпись плитки на хабе) */
export const corrWaiting = (games: readonly CorrGame[]): number => games.filter((g) => g.myMove).length;

/**
 * Открыть партию из уведомления (?corr=КОД или тап по уведомлению при открытом приложении): список с сервера,
 * затем сама партия; если мы в этой партии не играем — экран партий (там набор, куда можно войти).
 */
export async function openCorrFromLink(code: string, repaint: () => void): Promise<void> {
  if (st.net?.code === code) return;
  ui.home = 'corr';
  await loadCorr(repaint);
  if (st.corr.data?.games.some((g) => g.code === code)) {
    // хотсит-партия сохраняется (к ней можно вернуться), из другой онлайн-комнаты — выходим
    if (st.net) st.net.leave();
    else if (st.G) save();
    st.net = null;
    st.G = null;
    openCorr(code);
  }
}
